/**
 * TextFormatter - 文本格式化工具
 *
 * 1.6.0: 大幅精简 — 仅保留 fixOrderedListFormat
 * 
 * 背景：
 * - v1.3.44 引入 XMarkdown（@ant-design/x-markdown），已接管所有 Markdown 渲染：
 *   加粗标记、编号列表自动排序、段落分隔
 * - processBoldMarkers / formatRestaurantText / formatRestaurantNames / normalizeTextFormat
 *   均为 XMarkdown 之前的遗留代码，已成为死代码或干扰 XMarkdown 渲染
 * - 仅保留 fixOrderedListFormat：处理 LLM 流式输出中"序号和内容拆行"的特殊情况
 *   （XMarkdown 无法自动合并，需要预处理）
 */

/**
 * 修复有序列表格式
 * 
 * 处理 LLM 流式输出中的常见问题：
 * - 序号单独一行、内容在下一行 → 合并为同一行
 * - 序号和内容之间缺少空格 → 补充空格
 * - 列表项之间的多余空行 → 移除
 * - 不完整的列表项 → 尝试与下一行内容合并
 * 
 * 注意：不做编号重排（lazy numbering 由 XMarkdown 自动处理）
 */
export function fixOrderedListFormat(text: string): string {
  if (!text) return text;

  // Step 1: 处理序号单独一行的情况，将其与下一行合并
  // 匹配模式：数字. 单独一行（可能只有空格），后面跟着内容行
  let processed = text.replace(/(^\d+[\.、]\s*)\n+([^\d\n\s])/gm, '$1$2');
  
  // Step 2: 处理序号后面直接跟着内容但没有空格的情况，添加空格
  processed = processed.replace(/(^\d+[\.、])([^\s\n])/gm, '$1 $2');
  
  // Step 3: 处理序号后面跟着内容但内容在下一行的情况
  // 匹配：数字. 后面直接换行，然后跟着内容
  processed = processed.replace(/(^\d+[\.、]\s*)\n([^\d\n])/gm, '$1$2');

  const lines = processed.split('\n');
  const fixedLines: string[] = [];
  let inList = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();

    // 检测列表项：以数字开头，后跟点或顿号
    const listItemMatch = trimmedLine.match(/^(\d+)[\.、]\s*(.*)$/);
    
    if (listItemMatch) {
      const itemNumber = parseInt(listItemMatch[1], 10);
      let itemContent = listItemMatch[2];

      // 如果内容为空，尝试合并下一行
      if (!itemContent && i + 1 < lines.length) {
        const nextLine = lines[i + 1].trim();
        if (nextLine && !/^\d+[\.、]/.test(nextLine)) {
          // 下一行不是列表项，合并到当前项
          itemContent = nextLine;
          i++; // 跳过下一行
        }
      }
      
      // 统一格式：保持原始序号 + ". " + 内容（确保在同一行）
      const formattedItem = `${itemNumber}. ${itemContent}`;
      fixedLines.push(formattedItem);
      inList = true;
    } else {
      // 非列表项
      if (trimmedLine === '') {
        // 空行：如果在列表中，跳过（移除列表项之间的空行）
        // 如果不在列表中，保留
        if (!inList) {
          fixedLines.push('');
        }
      } else {
        // 有内容的行
        if (inList) {
          // 如果在列表中，这可能是列表项的续行
          // 检查上一行是否是列表项
          const lastLine = fixedLines[fixedLines.length - 1];
          if (lastLine && /^\d+\.\s/.test(lastLine)) {
            // 上一行是列表项，将当前行追加到上一行（用空格连接）
            fixedLines[fixedLines.length - 1] = `${lastLine} ${trimmedLine}`;
            continue;
          }
          // 否则结束列表状态
          inList = false;
        }
        fixedLines.push(line);
      }
    }
  }

  return fixedLines.join('\n');
}
